import React from 'react';
import { Stack } from '../../components/Stack/Stack';
import { FormSearch } from '../../components/Form/AsyncSearch/FormSearch';
import { t } from '../../locales/translate';
import { Grid } from '../../components/Grid/Grid';
import { FormTextField } from '../../components/Form/TextField/FormTextField';
import { FormSelect } from '../../components/Form/Select/FormSelect';
import { FormNumberField } from '../../components/Form/NumberField/FormNumberField';
import { FormFileUpload } from '../../components/Form/FileUpload/FormFileUpload';
import { Spacer } from '../../components/Spacer/Spacer';
import { FormCheckbox } from '../../components/Form/Checkbox/FormCheckbox';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { FormTextArea } from '../../components/Form/TextArea/FormTextArea';
import inverseExample from '../../assets/inverse_example.png';
import { MappedFields } from '@nerdgeschoss/react-use-form-library/dist/field';
import { NewsEdit } from '../../model/news';
import { Icon } from '../../components/Icon/Icon';

export type NewsFormFields = Pick<
  NewsEdit,
  'shopId' | 'titleDe' | 'titleEn' | 'textDe' | 'textEn' | 'category' | 'url' | 'inverse' | 'order' | 'asset' | 'meta'
>;

interface Props {
  fields: MappedFields<NewsFormFields>;
}

export function NewsForm({ fields }: Props): JSX.Element {
  return (
    <Stack>
      <FormSearch
        name="shopName"
        label={t.model.shop._name}
        searchIn={['shop']}
        {...fields.shopId}
        onChange={(shopId) => {
          shopId && fields.shopId.onChange(shopId);
        }}
      />
      <Grid gap="default">
        <FormTextField {...fields.titleEn} label={`${t.general.title} (${t.general.english})`} name="titleEn" />
        <FormTextField {...fields.titleDe} label={`${t.general.title} (${t.general.german})`} name="titleDe" />
      </Grid>
      <FormTextField {...fields.url} label={t.general.url} name="url" />
      <FormSelect
        {...fields.category}
        allowEmpty
        label={t.model.category._name}
        options={[
          {
            value: 'regular',
            label: t.model.news.regular,
          },
          {
            value: 'hot',
            label: t.model.news.hotNews,
          },
          {
            value: 'feature',
            label: t.model.news.featureNews,
          },
        ]}
        name="category"
      />
      <FormNumberField {...fields.order} label={t.model.news.order} name="order" />
      <FormFileUpload {...fields.asset} label={t.model.news.image_video} accept="video/*, image/*" name="asset" />
      <Spacer size="none" />
      <FormCheckbox
        {...fields.inverse}
        onChange={(value) => value !== undefined && fields.inverse.onChange(value)}
        label={
          <span className="news-form__inverse">
            <span>{t.model.news.inverse} </span>
            <Tooltip position="top-start" reference={<Icon name="question--sm" />}>
              <div className="news-form__inverse-example">
                <div>{t.model.news.inverseDescription}</div>
                <strong>{t.general.example}</strong>
                <img src={inverseExample}></img>
              </div>
            </Tooltip>
          </span>
        }
      />
      <Grid gap="default">
        <FormTextArea {...fields.textEn} label={`${t.general.text} (${t.general.english})`} name="textEn" />
        <FormTextArea {...fields.textDe} label={`${t.general.text} (${t.general.german})`} name="textDe" />
      </Grid>
      <FormTextArea {...fields.meta} label={t.model.news.meta} name="meta" monospace />
    </Stack>
  );
}
